import type { IVerifyEnrollmentAndAuthorizeResponse } from "@mne-authentication/api";

import type { TAuthorization, TAuthorizationActionType, TStoredAuth } from "./authStateTypes";
import { DO_NOT_USE_OUTSIDE_OF_authStateUtils as storage } from "./storageHelpers";

/* ==================================================================
 * Type and map unique to Auth
 * ================================================================== */

type TAuthItem = IVerifyEnrollmentAndAuthorizeResponse["authorizations"][number];
const IDENTIFIER_TO_ACTION: Record<TAuthItem["identifier"], TAuthorizationActionType | null> = {
  enrollPublicKeyMobile: "APP_ENROLL",
  enrollPublicKey: "WEBAUTHN_ENROLL",
  initializeTOTP: "TOTP_ENROLL",
  setPassword: null, // not needed for postEnrollment flow
};

/* ==================================================================
 * MAIN UTILS - Utils that update sessionStorage related to auth tokens
 * The corresponding utils in User repo are not identical, but must remain compatible
 * ================================================================== */

/**
 * Initialize state for post-enrollment dialog
 * - Use after enrollment to store the user's auth tokens
 * - This state will be consumed in User page to allow the user to set up several auth methods.
 */
export const createPostEnrollmentAuthorizationState = <T>(
  authsFromBackend: IVerifyEnrollmentAndAuthorizeResponse["authorizations"],
): TStoredAuth<unknown>["postEnrollmentData"] => {
  const authorizations: TAuthorization[] = authsFromBackend.map((a) => {
    const action = IDENTIFIER_TO_ACTION?.[a.identifier];
    const operationString = a.operation;
    return {
      userAction: action ?? a.identifier, // Fallback to identifier from backend (also unique)
      operationString,
      token: a.authorization,
      expirationTime: a.expires,
      isSpent: false,
    } as TAuthorization;
  });

  const data: TStoredAuth<T> = {
    postEnrollmentData: { authorizations },
  };
  storage.write(data);
  return data.postEnrollmentData;
};

/**
 * Update partial state for a single authorization with token and string
 * - This state will be consumed in User page to authorize the user's action (e.g. creating an API key)
 */
export const updateAuthorizationState = <T>(args: { authToken: string; expires: number }): void => {
  const parsed = storage.read<TStoredAuth<T>>();

  parsed.authorizationData.authorization.token = args?.authToken;
  parsed.authorizationData.authorization.expirationTime = args?.expires;

  storage.write(parsed);
};
